import * as React from "react";
import { LocalReaderSession, type LocalReaderPolicy, type ReaderState } from "./session";

const DISCONNECTED: ReaderState = { connected: false, busy: false, snapshot: null, error: "", errorSource: null, origin: null };
const noSubscription = (): (() => void) => () => {};
const disconnected = (): ReaderState => DISCONNECTED;

const Context = React.createContext<LocalReaderSession | null>(null);
export function LocalReaderProvider({
  children,
  policy,
}: {
  children: React.ReactNode;
  policy?: Partial<LocalReaderPolicy>;
}) {
  const [session] = React.useState(() => new LocalReaderSession(policy));
  React.useEffect(() => () => session.disconnect(), [session]);
  return <Context.Provider value={session}>{children}</Context.Provider>;
}
/**
 * The reader when its provider is mounted, else `null` — for a surface that
 * only MENTIONS the reader (the tracer list's pinned row) and renders without
 * the workspace provider in tests.
 */
export function useOptionalLocalReader() {
  const session = React.useContext(Context);
  const state = React.useSyncExternalStore(
    session?.subscribe ?? noSubscription,
    session?.getSnapshot ?? disconnected,
    session?.getSnapshot ?? disconnected,
  );
  return session ? { ...state, session } : null;
}

export function useLocalReader() {
  const session = React.useContext(Context);
  if (!session)
    throw new Error(
      "Local reader requires the authenticated workspace provider"
    );
  const state = React.useSyncExternalStore(
    session.subscribe,
    session.getSnapshot,
    session.getSnapshot
  );
  return { ...state, session };
}
